import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MoreHorizontal,
  Trash2,
  UserPen,
  Bookmark,
  UserX,
  ShieldOff,
  MessageSquareOff,
  Sparkles,
} from "lucide-react";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { Seo } from "@/lib/useSeo";

/**
 * HelpDeleteAccountData — polite, friendly guide explaining how a signed-in
 * BrontoY user can remove specific data (posts, different views, saved
 * items, profile fields, blocks/follows) without deleting the full account.
 *
 * All in-flow "sign in" mentions link back to the pre-signup Landing at `/`
 * because these controls only exist inside the app after authentication.
 * A prominent Y-logo pill sits at the very bottom for the same purpose.
 */
export default function HelpDeleteAccountData() {
  const nav = useNavigate();
  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col relative overflow-hidden"
      data-testid="help-delete-data-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="Delete Account Data — BrontoY"
        description="Learn how to remove specific posts, comments, saved items, and profile data from your BrontoY account."
        canonicalPath="/help/delete-account-data"
      />

      {/* Warm yellow ambient glow — BrontoY yellow accent theme */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "-10%",
            width: "720px",
            height: "720px",
            background:
              "radial-gradient(closest-side, rgba(250,204,21,0.22), transparent)",
            filter: "blur(60px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30%",
            right: "-15%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(245,158,11,0.18), transparent)",
            filter: "blur(60px)",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 h-14 px-4 md:px-8 flex items-center justify-between border-b border-white/10">
        <button
          type="button"
          onClick={() => nav("/help")}
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13.5px] font-medium"
          data-testid="help-delete-data-back"
        >
          <ArrowLeft size={16} strokeWidth={2.4} /> Back
        </button>
        <button
          type="button"
          onClick={() => nav("/")}
          aria-label="BrontoY home"
        >
          <img
            src={yLogoDark}
            alt="BrontoY"
            className="h-7 w-auto"
            draggable={false}
          />
        </button>
        <span className="w-10" />
      </header>

      <main className="relative z-10 flex-1 flex justify-center px-6 py-12 md:py-16">
        <div className="w-full max-w-[760px]">
          {/* Hero */}
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300/70 mb-4">
              Data control
            </p>
            <h1
              className="text-[34px] md:text-[46px] lg:text-[52px] font-extrabold tracking-tight leading-[1.05]"
              data-testid="help-delete-data-title"
            >
              Remove specific
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg,#fde047,#facc15,#f59e0b)",
                }}
              >
                data from BrontoY.
              </span>
            </h1>
            <p className="mt-5 text-white/65 text-[15px] md:text-[16.5px] leading-relaxed max-w-[560px] mx-auto">
              You can delete individual posts, different views, saved items and
              other pieces of data yourself — right from inside the app —
              without deleting your entire BrontoY account. Here&apos;s how.
            </p>
            <p className="mt-3 text-white/45 text-[13.5px] max-w-[520px] mx-auto">
              You&apos;ll need to be{" "}
              <SignInInline text="signed in" /> on your BrontoY account first.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-5 md:space-y-6">
            <GuideCard
              testId="guide-delete-post"
              Icon={Trash2}
              accent="from-rose-400 to-pink-500"
              title="1. Delete a post"
              steps={[
                <>
                  Once you are{" "}
                  <SignInInline text="signed in" />, open your profile from the
                  bottom tab bar (or click your avatar on the sidebar).
                </>,
                <>
                  Tap any post you would like to remove — this opens it in the
                  post detail view.
                </>,
                <>
                  Look for the{" "}
                  <InlineIcon Icon={MoreHorizontal} /> three-dot menu in the top
                  right of the post header.
                </>,
                <>
                  Tap it and choose{" "}
                  <span className="text-red-300 font-semibold">Delete post</span>{" "}
                  from the menu. Confirm — the post and all of its likes,
                  agrees, saves and different views are gone permanently.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-delete-reply"
              Icon={MessageSquareOff}
              accent="from-fuchsia-400 to-purple-500"
              title="2. Delete your reply on a standard post"
              steps={[
                <>
                  Open the standard post that carries the reply you posted.
                </>,
                <>
                  Scroll to the replies section beneath the post. Locate your
                  own reply — only your own replies show a delete control.
                </>,
                <>
                  Tap the{" "}
                  <InlineIcon Icon={Trash2} /> trash icon in the top-right of
                  your reply card. Your reply is removed instantly for
                  everyone.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-delete-different-view"
              Icon={MessageSquareOff}
              accent="from-violet-400 to-indigo-500"
              title="3. Delete your different view on an opinion post"
              steps={[
                <>
                  Open the opinion post — tap the outlined opinion box to open
                  the different views panel.
                </>,
                <>
                  Scroll through the list and find your own different view.
                  Only your own entries carry a trash control.
                </>,
                <>
                  Tap the{" "}
                  <InlineIcon Icon={Trash2} /> trash icon beside your different
                  view. Confirm the prompt — the different view is removed
                  permanently.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-delete-saved"
              Icon={Bookmark}
              accent="from-amber-300 to-yellow-500"
              title="4. Clear saved posts"
              steps={[
                <>
                  Open the <span className="font-semibold">Saved</span> tab from
                  your profile or the sidebar.
                </>,
                <>
                  Tap any saved post to open it, then hit the{" "}
                  <InlineIcon Icon={Bookmark} /> filled bookmark icon to
                  un-save it. The post disappears from your saved list
                  immediately — the original post itself is untouched.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-edit-profile"
              Icon={UserPen}
              accent="from-cyan-300 to-sky-500"
              title="5. Remove or edit profile info"
              steps={[
                <>
                  Head to{" "}
                  <span className="font-semibold">
                    Settings → Edit Profile
                  </span>
                  .
                </>,
                <>
                  Clear or change your display name, bio, links, or date of
                  birth. Tap the profile photo to remove your avatar or upload
                  a new one.
                </>,
                <>
                  Save — the changes go live instantly on your public profile
                  card.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-unfollow-block"
              Icon={UserX}
              accent="from-orange-400 to-red-500"
              title="6. Trim followers & unfollow accounts"
              steps={[
                <>
                  Open your profile and tap{" "}
                  <span className="font-semibold">Followers</span> or{" "}
                  <span className="font-semibold">Following</span>.
                </>,
                <>
                  For anyone you no longer want to follow, tap{" "}
                  <span className="font-semibold">Following</span> next to their
                  name — it flips to{" "}
                  <span className="font-semibold">Follow</span> and they are
                  removed from your following list.
                </>,
                <>
                  To remove someone who follows you, open their profile, hit the{" "}
                  <InlineIcon Icon={MoreHorizontal} /> three-dot menu and choose{" "}
                  <span className="text-red-300 font-semibold">Block user</span>{" "}
                  — this also drops them from your followers.
                </>,
              ]}
            />

            <GuideCard
              testId="guide-blocked-list"
              Icon={ShieldOff}
              accent="from-emerald-400 to-teal-500"
              title="7. Manage or clear your blocked list"
              steps={[
                <>
                  Go to{" "}
                  <span className="font-semibold">
                    Settings → Blocked users
                  </span>{" "}
                  to see everyone you&apos;ve blocked.
                </>,
                <>
                  Tap{" "}
                  <span className="font-semibold">Unblock</span> beside any
                  entry to remove them from your block list — the record is
                  cleared from your account.
                </>,
              ]}
            />
          </div>

          {/* Rescue block — full account deletion pointer */}
          <div
            className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-7"
            data-testid="guide-full-delete-callout"
          >
            <div className="flex items-start gap-4">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(244,63,94,0.25), rgba(190,18,60,0.15))",
                  border: "1px solid rgba(244,63,94,0.35)",
                }}
              >
                <Sparkles size={20} className="text-red-300" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h3 className="text-white text-[17px] md:text-[18px] font-extrabold tracking-tight">
                  Prefer to remove everything at once?
                </h3>
                <p className="text-white/60 text-[14px] mt-1.5 leading-relaxed">
                  If you want to permanently erase your entire account — posts,
                  different views, saves, followers, all of it —{" "}
                  <Link
                    to="/help/delete"
                    className="text-red-300 underline underline-offset-4 hover:text-red-200"
                    data-testid="guide-goto-full-delete"
                  >
                    start the full account deletion flow
                  </Link>{" "}
                  instead.
                </p>
              </div>
            </div>
          </div>

          {/* Y-logo pill Sign In — bottom-most CTA */}
          <div className="mt-14 flex flex-col items-center">
            <p className="text-white/50 text-[13.5px] mb-4 text-center max-w-[420px]">
              Ready to manage your data? Sign in to BrontoY and follow any of
              the steps above.
            </p>
            <button
              type="button"
              onClick={() => nav("/")}
              className="group inline-flex items-center gap-3 h-[54px] pl-2.5 pr-6 rounded-full text-black font-extrabold text-[15.5px] transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background:
                  "linear-gradient(180deg, #fde047 0%, #facc15 55%, #f59e0b 100%)",
                boxShadow:
                  "0 20px 60px -18px rgba(250,204,21,0.65), 0 8px 24px -10px rgba(0,0,0,0.6)",
              }}
              data-testid="help-delete-data-signin"
            >
              <span
                className="w-[38px] h-[38px] rounded-full flex items-center justify-center overflow-hidden"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, #1a1a1a, #000)",
                }}
              >
                <img
                  src={yLogoDark}
                  alt=""
                  className="h-6 w-auto"
                  draggable={false}
                />
              </span>
              <span className="tracking-tight">Sign in to BrontoY</span>
              <span
                aria-hidden
                className="ml-1 text-[16px] transition-transform duration-300 group-hover:translate-x-0.5"
              >
                →
              </span>
            </button>
            <p className="mt-4 text-white/35 text-[12px]">
              BrontoY <span className="text-white/25">·</span> By Bronto
              Intelligence
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ----------------------- inline helpers ----------------------- */

function SignInInline({ text = "sign in" }) {
  return (
    <Link
      to="/"
      className="font-semibold text-yellow-300 hover:text-yellow-200 underline underline-offset-4 decoration-yellow-400/40 hover:decoration-yellow-300"
      data-testid="help-delete-data-inline-signin"
    >
      {text}
    </Link>
  );
}

function InlineIcon({ Icon }) {
  return (
    <span
      className="inline-flex items-center justify-center align-middle w-6 h-6 rounded-md bg-white/10 border border-white/10 mx-0.5"
      aria-hidden
    >
      <Icon size={13} strokeWidth={2.2} className="text-white/85" />
    </span>
  );
}

function GuideCard({ testId, Icon, accent, title, steps }) {
  return (
    <section
      className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.015] p-6 md:p-7 backdrop-blur-sm"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-[0_10px_30px_-14px_rgba(0,0,0,0.9)]`}
        >
          <Icon size={20} className="text-white" strokeWidth={2.2} />
        </div>
        <h2 className="text-white text-[19px] md:text-[21px] font-extrabold tracking-tight">
          {title}
        </h2>
      </div>
      <ol className="space-y-3.5 text-white/75 text-[14.5px] leading-[22px]">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span
              className="mt-0.5 shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 border border-white/10 text-[12px] font-bold text-white/80"
              aria-hidden
            >
              {i + 1}
            </span>
            <span className="min-w-0 break-words">{s}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
